import React, { useEffect } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import { Bold, Italic, List, ListOrdered, Link as LinkIcon } from 'lucide-react';
import { cn } from '../../lib/utils';

const Btn = ({ onClick, isActive, children, title }) => (
  <button
    type="button"
    onClick={onClick}
    title={title}
    className={cn(
      "p-1.5 rounded transition-colors hover:bg-gray-200 dark:hover:bg-gray-700",
      isActive && "bg-purple-100 dark:bg-purple-900/40 text-purple-600 dark:text-purple-400"
    )}
  >
    {children}
  </button>
);

const Toolbar = ({ editor }) => {
  if (!editor) return null;

  const addLink = () => {
    const url = prompt('Enter URL:');
    if (url) editor.chain().focus().setLink({ href: url }).run();
  };

  return (
    <div className="flex items-center gap-0.5 px-2 py-1.5 border-b border-gray-200 dark:border-gray-700">
      <Btn onClick={() => editor.chain().focus().toggleBold().run()} isActive={editor.isActive('bold')} title="Bold">
        <Bold className="w-3.5 h-3.5" />
      </Btn>
      <Btn onClick={() => editor.chain().focus().toggleItalic().run()} isActive={editor.isActive('italic')} title="Italic">
        <Italic className="w-3.5 h-3.5" />
      </Btn>
      <div className="w-px h-4 bg-gray-300 dark:bg-gray-600 mx-1" />
      <Btn onClick={() => editor.chain().focus().toggleBulletList().run()} isActive={editor.isActive('bulletList')} title="Bullet List">
        <List className="w-3.5 h-3.5" />
      </Btn>
      <Btn onClick={() => editor.chain().focus().toggleOrderedList().run()} isActive={editor.isActive('orderedList')} title="Numbered List">
        <ListOrdered className="w-3.5 h-3.5" />
      </Btn>
      <div className="w-px h-4 bg-gray-300 dark:bg-gray-600 mx-1" />
      <Btn onClick={addLink} isActive={editor.isActive('link')} title="Add Link">
        <LinkIcon className="w-3.5 h-3.5" />
      </Btn>
    </div>
  );
};

const DescriptionEditor = ({ content, onChange, placeholder = 'Enter description...' }) => {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: false, blockquote: false, codeBlock: false, horizontalRule: false }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: { class: 'text-purple-600 underline hover:text-purple-800' },
      }),
      Placeholder.configure({ placeholder }),
    ],
    content: content || '',
    editorProps: {
      attributes: {
        class: 'prose prose-sm max-w-none dark:prose-invert focus:outline-none px-3 py-2 min-h-[100px] text-sm prose-p:my-1 prose-ul:my-1 prose-ol:my-1 prose-li:my-0',
      },
    },
    onUpdate: ({ editor }) => {
      if (onChange) onChange(editor.getHTML());
    },
  });

  useEffect(() => {
    if (editor && content !== undefined && content !== editor.getHTML()) {
      editor.commands.setContent(content || '');
    }
  }, [content, editor]);

  return (
    <div className="border rounded-lg overflow-hidden bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-700">
      <Toolbar editor={editor} />
      <EditorContent editor={editor} />
    </div>
  );
};

export default DescriptionEditor;
